import { type RoomEvent, SseParser, StreamFrame, StreamHello } from "@br9704/aethereum-shared";

/**
 * HostedSubscriber — the CLI side of the hosted realtime spine.
 *
 * Holds `GET /api/sync/stream` (SSE) open against the hosted API, resuming from
 * the last seen event id across reconnects (exponential backoff). When the
 * stream can't be established it silently degrades to polling
 * `GET /api/sync/events?since=` — per the guardrail, realtime loss must NEVER
 * affect the agent. Own events (per the hello frame's memberId) are filtered
 * out before delivery.
 */

export interface SubscriberDelivery {
  event: RoomEvent;
  id: number;
  /** Display handle for event.memberId when the roster knows it. */
  handle?: string;
}

export interface HostedSubscriberOptions {
  baseUrl: string;
  token: string;
  onEvent: (d: SubscriberDelivery) => void;
  onStatus?: (status: "sse" | "polling" | "reconnecting") => void;
  /** Resume cursor (last seen event id). Default 0 = roster catch-up from start. */
  since?: number;
  /** Deliver this token's own member events too (observers like `aethereum watch`). */
  includeOwn?: boolean;
  fetchImpl?: typeof fetch;
  /** Backoff bounds (ms). */
  minBackoffMs?: number;
  maxBackoffMs?: number;
  pollIntervalMs?: number;
}

export class HostedSubscriber {
  private readonly base: string;
  private cursor: number;
  private hello: StreamHello | null = null;
  private stopped = false;
  private abort: AbortController | null = null;
  private readonly fetchImpl: typeof fetch;
  private readonly minBackoff: number;
  private readonly maxBackoff: number;
  private readonly pollInterval: number;

  constructor(private readonly opts: HostedSubscriberOptions) {
    this.base = opts.baseUrl.replace(/\/$/, "");
    this.cursor = opts.since ?? 0;
    this.fetchImpl = opts.fetchImpl ?? fetch;
    this.minBackoff = opts.minBackoffMs ?? 1_000;
    this.maxBackoff = opts.maxBackoffMs ?? 30_000;
    this.pollInterval = opts.pollIntervalMs ?? 5_000;
  }

  get lastCursor(): number {
    return this.cursor;
  }

  /** The room roster from the latest hello frame (memberId → handle). */
  get roster(): Record<string, string> {
    return this.hello?.roster ?? {};
  }

  /** Runs until stop(). Never throws — every failure path degrades silently. */
  async start(): Promise<void> {
    let backoff = this.minBackoff;
    while (!this.stopped) {
      const connected = await this.runSse().catch(() => false);
      if (this.stopped) return;

      if (connected) {
        backoff = this.minBackoff; // stream worked at least once — reset
      } else {
        // SSE unavailable — one poll round keeps context flowing
        this.opts.onStatus?.("polling");
        await this.pollOnce().catch(() => {});
        if (this.stopped) return;
      }

      this.opts.onStatus?.("reconnecting");
      await this.sleep(connected ? this.minBackoff : backoff);
      backoff = Math.min(backoff * 2, this.maxBackoff);
    }
  }

  stop(): void {
    this.stopped = true;
    this.abort?.abort();
  }

  /** One SSE connection; resolves true if frames flowed (i.e. SSE works here). */
  private async runSse(): Promise<boolean> {
    this.abort = new AbortController();
    const res = await this.fetchImpl(`${this.base}/api/sync/stream?since=${this.cursor}`, {
      headers: { Authorization: `Bearer ${this.opts.token}`, Accept: "text/event-stream" },
      signal: this.abort.signal,
    });
    if (!res.ok || !res.body) return false;

    this.opts.onStatus?.("sse");
    const parser = new SseParser();
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let sawFrames = false;

    try {
      for (;;) {
        const { done, value } = await reader.read();
        if (done || this.stopped) break;
        for (const msg of parser.push(decoder.decode(value, { stream: true }))) {
          sawFrames = true;
          this.handleMessage(msg.event, msg.data);
        }
      }
    } finally {
      reader.releaseLock();
    }
    return sawFrames;
  }

  private handleMessage(event: string | undefined, data: string): void {
    try {
      if (event === "hello") {
        this.hello = StreamHello.parse(JSON.parse(data));
        if (this.cursor === 0) this.cursor = this.hello.cursor; // fresh start: tail only
        return;
      }
      const frame = StreamFrame.parse(JSON.parse(data));
      this.deliver(frame);
    } catch {
      // malformed frame — skip, never break the loop
    }
  }

  private deliver(frame: StreamFrame): void {
    if (frame.id <= this.cursor) return;
    this.cursor = frame.id;
    const memberId = "memberId" in frame.event ? frame.event.memberId : undefined;
    if (!this.opts.includeOwn && memberId && memberId === this.hello?.memberId) return; // own event
    this.opts.onEvent({
      event: frame.event,
      id: frame.id,
      handle: memberId ? this.hello?.roster[memberId] : undefined,
    });
  }

  /** Polling fallback round: drain frames until caught up, then return. */
  private async pollOnce(): Promise<void> {
    for (let i = 0; i < 10 && !this.stopped; i++) {
      const res = await this.fetchImpl(`${this.base}/api/sync/events?since=${this.cursor}`, {
        headers: { Authorization: `Bearer ${this.opts.token}` },
      });
      if (!res.ok) return;
      const body = (await res.json()) as { events: StreamFrame[]; cursor: number };
      if (!body.events?.length) return;
      for (const raw of body.events) {
        const frame = StreamFrame.safeParse(raw);
        if (frame.success) this.deliver(frame.data);
      }
      await this.sleep(this.pollInterval);
    }
  }

  private sleep(ms: number): Promise<void> {
    return new Promise((resolve) => {
      const t = setTimeout(resolve, ms);
      // don't hold the process open for a backoff timer
      if (typeof t === "object" && "unref" in t) t.unref();
    });
  }
}

/**
 * Presence heartbeat: POST /api/sync/presence every `intervalMs` (first beat
 * immediately). Fire-and-forget; returns a stop function.
 */
export function startPresenceHeartbeat(
  baseUrl: string,
  token: string,
  opts: { building?: () => string | undefined; intervalMs?: number; fetchImpl?: typeof fetch } = {},
): () => void {
  const base = baseUrl.replace(/\/$/, "");
  const fetchImpl = opts.fetchImpl ?? fetch;
  const beat = () => {
    void fetchImpl(`${base}/api/sync/presence`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ building: opts.building?.() }),
    }).catch(() => {
      // silent — presence loss never affects the agent
    });
  };
  beat();
  const timer = setInterval(beat, opts.intervalMs ?? 30_000);
  if (typeof timer === "object" && "unref" in timer) timer.unref();
  return () => clearInterval(timer);
}
